/**
 * Company Data-Aggregation Project API
 *
 * Clients for /api/companies/:id/projects — the data-aggregation projects
 * introduced by Spec 2026-04-15-company-projects-integrations. These are
 * distinct from Specwright code-repo projects (see company-api.ts
 * CompanySpecwrightProject).
 */

export interface CompanyDataProject {
  id: number;
  company_id: number;
  name: string;
  url: string | null;
  created_at: string;
  updated_at: string;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function listDataProjects(companyId: number): Promise<CompanyDataProject[]> {
  return fetchJson<CompanyDataProject[]>(`/api/companies/${companyId}/projects`);
}

export async function createDataProject(
  companyId: number,
  data: { name: string; url?: string | null },
): Promise<CompanyDataProject> {
  return fetchJson<CompanyDataProject>(`/api/companies/${companyId}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateDataProject(
  companyId: number,
  projectId: number,
  data: { name?: string; url?: string | null },
): Promise<CompanyDataProject> {
  return fetchJson<CompanyDataProject>(`/api/companies/${companyId}/projects/${projectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteDataProject(companyId: number, projectId: number): Promise<void> {
  const response = await fetch(`/api/companies/${companyId}/projects/${projectId}`, {
    method: 'DELETE',
  });
  if (response.status !== 204) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Delete failed: ${response.status}`);
  }
}
